import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MagnifyingGlass, WhatsappLogo, Envelope, Phone, ArrowsClockwise, PencilSimple, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";

const BLANK = { customer_name: "", company_name: "", mobile: "", whatsapp: "", email: "",
  gst: "", pan: "", address: "", city: "", state: "", pincode: "", business_type: "",
  source: "Website", category: "Retail", notes: "" };

export default function Customers() {
  const { user, isAdmin, can, canEdit, canDelete } = usePermissions();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [selected, setSelected] = useState(new Set());
  const [xferOpen, setXferOpen] = useState(false);
  const [xfer, setXfer] = useState({ new_owner_id: "", reason: "", cascade: true });

  const [editingId, setEditingId] = useState(null);
  const load = async () => {
    const r = await api.get("/customers", { params: q ? { q } : {} });
    setItems(r.data);
  };
  const loadTeam = async () => {
    try { const r = await api.get("/users"); setTeam(r.data.filter((u) => u.active !== false)); }
    catch (err) {
      // non-owner may not access /users — expected. Log unexpected errors only.
      if (err.response?.status !== 403 && process.env.NODE_ENV !== "production") {
        console.warn("loadTeam failed", err);
      }
    }
  };
  useEffect(() => { load(); }, [q]);  // eslint-disable-line
  useEffect(() => { if (isAdmin) loadTeam(); }, [isAdmin]);  // eslint-disable-line

  const save = async () => {
    if (!form.customer_name || !form.mobile) return toast.error("Name & mobile required");
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setOpen(false); setForm(BLANK); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (c) => { setForm({ ...BLANK, ...c }); setEditingId(c.id); setOpen(true); };
  const remove = async (c) => {
    if (!confirm(`Delete "${c.customer_name}"? It will move to the recycle bin.`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete not allowed"); }
  };

  const toggleSel = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((c) => c.id)));
  };

  const submitTransfer = async () => {
    if (!xfer.new_owner_id) return toast.error("Pick a new owner");
    if (selected.size === 0) return toast.error("Select at least one customer");
    try {
      const r = await api.post("/customers/transfer-ownership", {
        customer_ids: [...selected],
        new_owner_id: xfer.new_owner_id,
        reason: xfer.reason || "",
        cascade: xfer.cascade,
      });
      toast.success(`Transferred ${r.data.transferred} customer(s)`);
      setXferOpen(false); setXfer({ new_owner_id: "", reason: "", cascade: true });
      setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };

  const teamById = Object.fromEntries(team.map((t) => [t.id, t]));
  const ownerLabel = (c) => {
    if (!c.owner_id) return "—";
    const u = teamById[c.owner_id];
    return u ? u.name : c.owner_id.slice(0, 6);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Directory</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Customers</h1>
          <p className="text-sm text-muted-foreground mt-2">{items.length} contacts you can access</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && selected.size > 0 && (
            <Button variant="outline" className="rounded-full" onClick={() => setXferOpen(true)} data-testid="open-transfer-button">
              <ArrowsClockwise size={16} className="mr-2" />Transfer {selected.size} owner{selected.size > 1 ? "s" : ""}
            </Button>
          )}
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="pl-9 w-64" data-testid="customer-search" />
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(BLANK); setEditingId(null); } }}>
            {can("customers.create") && (
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-customer-button"><Plus size={16} className="mr-2" /> Add Customer</Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingId ? "Edit customer" : "New Customer"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                {[
                  ["customer_name","Customer Name *"],["company_name","Company"],
                  ["mobile","Mobile *"],["whatsapp","WhatsApp"],
                  ["email","Email"],["gst","GST Number"],
                  ["pan","PAN"],["business_type","Business Type"],
                  ["city","City"],["state","State"],
                  ["pincode","Pincode"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs">{label.toUpperCase()}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({...form,[k]:e.target.value})} data-testid={`field-${k}`} />
                  </div>
                ))}
                <div className="col-span-2">
                  <Label className="text-xs">ADDRESS</Label>
                  <Input value={form.address} onChange={(e) => setForm({...form,address:e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">SOURCE</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({...form,source:v})}>
                    <SelectTrigger data-testid="source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Website","Instagram","Google","WhatsApp","Referral","IndiaMART","Meta Leads"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">CATEGORY</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form,category:v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Retail","Wholesale","Dealer","Event Planner"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">NOTES</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({...form,notes:e.target.value})} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} data-testid="save-customer-button">{editingId ? "Save changes" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                {isAdmin && (
                  <th className="p-4 w-10">
                    <Checkbox checked={selected.size > 0 && selected.size === items.length} onCheckedChange={selectAll} data-testid="select-all-customers" />
                  </th>
                )}
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Company</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-left p-4">Location</th>
                <th className="text-left p-4">Owner</th>
                <th className="text-left p-4">Category</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-border/40 hover:bg-secondary/30" data-testid={`customer-row-${c.id}`}>
                  {isAdmin && (
                    <td className="p-4">
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`select-customer-${c.id}`} />
                    </td>
                  )}
                  <td className="p-4">
                    <Link to={`/customers/${c.id}`} className="font-medium hover:text-primary">
                      {c.customer_name}
                    </Link>
                  </td>
                  <td className="p-4 text-muted-foreground">{c.company_name || "—"}</td>
                  <td className="p-4">
                    <div className="text-xs">{c.mobile}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </td>
                  <td className="p-4 text-muted-foreground text-xs">{c.city}{c.state ? `, ${c.state}` : ""}</td>
                  <td className="p-4 text-xs" data-testid={`owner-${c.id}`}>{ownerLabel(c)}</td>
                  <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-secondary">{c.category}</span></td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-1">
                      {c.whatsapp && <a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-secondary rounded"><WhatsappLogo size={16} weight="duotone" /></a>}
                      {c.email && <a href={`mailto:${c.email}`} className="p-2 hover:bg-secondary rounded"><Envelope size={16} weight="duotone" /></a>}
                      {c.mobile && <a href={`tel:${c.mobile}`} className="p-2 hover:bg-secondary rounded"><Phone size={16} weight="duotone" /></a>}
                      <AttachmentsButton entityType="customer" entityId={c.id} label={`Files — ${c.customer_name}`} testId={`attach-customer-${c.id}`} />
                      {canEdit("customers.edit", c) && (
                        <button onClick={() => openEdit(c)} className="p-2 hover:bg-secondary rounded text-muted-foreground hover:text-primary" title="Edit" data-testid={`edit-customer-${c.id}`}><PencilSimple size={16} weight="duotone" /></button>
                      )}
                      {canDelete("customers.delete", c) && (
                        <button onClick={() => remove(c)} className="p-2 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive" title="Delete" data-testid={`delete-customer-${c.id}`}><Trash size={16} weight="duotone" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={isAdmin ? 8 : 7} className="p-10 text-center text-muted-foreground">No customers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {isAdmin && (
        <Dialog open={xferOpen} onOpenChange={setXferOpen}>
          <DialogContent className="max-w-md" data-testid="transfer-ownership-dialog">
            <DialogHeader><DialogTitle>Transfer customer ownership</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="text-sm text-muted-foreground">
                Reassigning <span className="font-semibold text-foreground">{selected.size}</span> customer{selected.size > 1 ? "s" : ""}. The original <em>created_by</em> is preserved. Related leads, tasks, follow-ups, quotations, orders and payments will follow when cascade is on.
              </div>
              <div>
                <Label className="text-xs">NEW OWNER</Label>
                <Select value={xfer.new_owner_id} onValueChange={(v) => setXfer({ ...xfer, new_owner_id: v })}>
                  <SelectTrigger data-testid="new-owner-select"><SelectValue placeholder="Pick a team member…" /></SelectTrigger>
                  <SelectContent>
                    {team.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name} · <span className="text-xs text-muted-foreground">{t.role.replace("_", " ")}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">REASON (audit log)</Label>
                <Input value={xfer.reason} onChange={(e) => setXfer({ ...xfer, reason: e.target.value })} placeholder="Employee departure, territory change, etc." data-testid="transfer-reason" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={xfer.cascade} onCheckedChange={(v) => setXfer({ ...xfer, cascade: !!v })} data-testid="cascade-checkbox" />
                <span>Also transfer related records (leads, tasks, quotations, orders, payments)</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setXferOpen(false)}>Cancel</Button>
              <Button onClick={submitTransfer} data-testid="submit-transfer-button">Transfer ownership</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
